import React, { useCallback, useMemo, useState } from "react";
import * as styles from "./site-detail-events.module.scss";
import { AnyVelavuEvent, EventCategory, VelavuAPI } from "velavu-js-api";
import DatePicker from "../../../../elements/date-picker";
import { DateRange, useDateRange } from "../../../../data/date-range";
import { useCancellableEffect } from "../../../../helper/hook-helper";
import Divider from "../../../../elements/divider";
import EventList from "../../../../elements/event-list";
import {
	compareEventAsc,
	compareEventDesc,
} from "../../../../helper/events-helper";
import { useProvideContentRefreshable } from "../../../../elements/refresh-context";

export interface SiteDetailEventsProps {
	siteID: string;
}

const SITE_EVENT_CATEGORIES: readonly EventCategory[] = [
	EventCategory.SiteEntered,
	EventCategory.SiteExited,
];

function loadSiteEvents(
	dateRange: DateRange,
	siteID: string,
): Promise<AnyVelavuEvent[]> {
	const promises = SITE_EVENT_CATEGORIES.map((eventCategory) => {
		return VelavuAPI.events.getAllEvents({
			category: eventCategory,
			since: dateRange.start,
			until: dateRange.end,
			siteID: siteID,
		});
	});

	return Promise.all(promises).then((results) => {
		return results.map((paginatedResult) => paginatedResult.data).flat();
	});
}

export default function SiteDetailEvents(props: SiteDetailEventsProps) {
	//Last week -> Today
	const [dateRange, setDateRange] = useDateRange(7);
	const [sortNew, setSortNew] = useState(true);

	//Load the events
	const [events, setEvents] = useState<AnyVelavuEvent[] | undefined>(
		undefined,
	);
	useCancellableEffect(
		(addPromise) => {
			addPromise(loadSiteEvents(dateRange, props.siteID))
				.then(setEvents)
				.catch(console.warn);
		},
		[setEvents, props.siteID, dateRange],
	);

	//Register the refresh callback
	const refreshEvents = useCallback(() => {
		return loadSiteEvents(dateRange, props.siteID).then(setEvents);
	}, [setEvents, props.siteID, dateRange]);
	useProvideContentRefreshable(refreshEvents);

	//Sort the events
	const sortedEvents = useMemo(() => {
		return events?.sort(sortNew ? compareEventDesc : compareEventAsc);
	}, [events, sortNew]);

	return (
		<>
			<DatePicker
				range={dateRange}
				onUpdate={setDateRange}
				sortNew={sortNew}
				setSortNew={setSortNew}
			/>

			<Divider />

			<EventList
				className={styles.list}
				events={sortedEvents}
				includeEventOrigin
			/>
		</>
	);
}
